import { useCallback, useEffect, useState } from 'react'
import {
  assignLesson,
  createStage,
  errorMessage,
  listSettings,
  listStages,
  reorderStages,
  updateStage,
} from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { LEVELS, LEVEL_FA, STATUS_FA } from './constants'
import { lessonAltName, lessonName } from './lessonTitle'
import { StatusBadge } from './components/primitives'
import {
  ARCADE_SETTING_FA,
  ARCADE_SETTING_KEYS,
  describeArcadeSetting,
  moved,
  publishedWithoutStage,
  renumberPlan,
  stageRefusal,
} from './stages'

// ─── Stages tab (admin-arcade, V7-p) ──────────────────────────────────────────

const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'
const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40'
const btnMove = 'rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-30'

const STATUSES = ['draft', 'published', 'archived']

/**
 * The unlock rules and star thresholds, read-only. They are runtime settings
 * (`arcade.*`, X5) and the «تنظیمات» tab is the one place that writes them —
 * a second editor here would be two sources of truth for one value, so this
 * shows what they are right now and says where they are changed.
 */
export function ArcadeRulesCard({ settings }) {
  const rows = (settings ?? []).filter(item => ARCADE_SETTING_KEYS.includes(item.key))
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-700">قانون‌های مسیر</h3>
      <p className="mt-1 text-xs text-slate-500">
        این مقدارها فقط برای خواندن‌اند؛ ویرایش‌شان در تب «تنظیمات» انجام می‌شود.
      </p>
      {rows.length === 0
        ? <p className="mt-2 text-xs text-slate-400">مقدارها هنوز خوانده نشده‌اند.</p>
        : (
          <dl className="mt-2 space-y-1.5">
            {ARCADE_SETTING_KEYS.map(key => {
              const row = rows.find(item => item.key === key)
              if (!row) return null
              return (
                <div key={key} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                  <dt className="font-medium text-slate-600">{ARCADE_SETTING_FA[key]}</dt>
                  <dd className="text-slate-800">{describeArcadeSetting(key, row.value)}</dd>
                  <span className="text-slate-300" dir="ltr">{key}</span>
                </div>
              )
            })}
          </dl>
        )}
    </section>
  )
}

/**
 * The published lessons of this level that are in no stage: live in the flat
 * catalogue, absent from the learner's path. Nothing is wrong with the data,
 * so this is a warning and not an error — but it is the one thing about a
 * path that is invisible unless the panel says it out loud.
 */
export function UnassignedWarning({ lessons }) {
  const orphans = publishedWithoutStage(lessons)
  if (orphans.length === 0) return null
  return (
    <div className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <p className="font-medium">
        {faDigits(orphans.length)} درس منتشرشده در هیچ مرحله‌ای نیست و روی مسیر زبان‌آموز دیده نمی‌شود:
      </p>
      <ul className="mt-1 list-disc space-y-0.5 ps-5 text-xs">
        {orphans.map(lesson => (
          <li key={lesson.id}>
            <span dir="ltr">{lessonName(lesson)}</span>
            {lessonAltName(lesson) && <span className="text-amber-700"> ({lessonAltName(lesson)})</span>}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs">هر کدام را از فهرست «افزودن درس» در یکی از مرحله‌های همین سطح اضافه کنید.</p>
    </div>
  )
}

/** Create a stage on this level's path. It is appended at the end; position is changed afterwards with ↑/↓. */
export function StageCreateForm({ level, busy, onCreate }) {
  const [titleFa, setTitleFa] = useState('')
  const [titleEn, setTitleEn] = useState('')
  const [theme, setTheme] = useState('')
  const [status, setStatus] = useState('draft')
  const [err, setErr] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (!titleFa.trim() || !titleEn.trim()) { setErr('هر دو عنوان فارسی و انگلیسی لازم است.'); return }
    setErr('')
    const dto = { level, titleFa: titleFa.trim(), titleEn: titleEn.trim(), status }
    if (theme.trim()) dto.theme = theme.trim()
    const ok = await onCreate(dto)
    if (ok) { setTitleFa(''); setTitleEn(''); setTheme('') }
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <h3 className="mb-2 text-sm font-semibold text-slate-700">مرحلهٔ جدید در {LEVEL_FA[level]}</h3>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">عنوان فارسی</span>
          <input type="text" value={titleFa} onChange={e => setTitleFa(e.target.value)} placeholder="کافه" className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">عنوان انگلیسی</span>
          <input type="text" value={titleEn} onChange={e => setTitleEn(e.target.value)} dir="ltr" placeholder="At the café" className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">تم (اختیاری)</span>
          <input type="text" value={theme} onChange={e => setTheme(e.target.value)} dir="ltr" placeholder="cafe" className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">وضعیت</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className={`w-full ${inputCls}`}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_FA[s]}</option>)}
          </select>
        </label>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>افزودن مرحله</button>
        <span className="text-xs text-slate-400">مرحله در انتهای مسیر ساخته می‌شود؛ جایش را با کلیدهای ↑ و ↓ عوض کنید.</span>
      </div>
      {err && <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
    </form>
  )
}

/** Titles, theme and status of an existing stage. The level is not here: a stage never moves level (move its lessons instead). */
export function StageEditForm({ stage, busy, onSave, onCancel }) {
  const [titleFa, setTitleFa] = useState(stage.titleFa)
  const [titleEn, setTitleEn] = useState(stage.titleEn)
  const [theme, setTheme] = useState(stage.theme ?? '')
  const [status, setStatus] = useState(stage.status)
  const [err, setErr] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (!titleFa.trim() || !titleEn.trim()) { setErr('هر دو عنوان فارسی و انگلیسی لازم است.'); return }
    setErr('')
    // `theme: null` clears it; the empty box means "no theme", not "leave as is".
    await onSave({ titleFa: titleFa.trim(), titleEn: titleEn.trim(), theme: theme.trim() || null, status })
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 rounded-lg bg-slate-50 px-3 py-2">
      <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">عنوان فارسی</span>
          <input type="text" value={titleFa} onChange={e => setTitleFa(e.target.value)} className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">عنوان انگلیسی</span>
          <input type="text" value={titleEn} onChange={e => setTitleEn(e.target.value)} dir="ltr" className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">تم</span>
          <input type="text" value={theme} onChange={e => setTheme(e.target.value)} dir="ltr" className={`w-full ${inputCls}`} />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">وضعیت</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className={`w-full ${inputCls}`}>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_FA[s]}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>ذخیره</button>
        <button type="button" disabled={busy} onClick={onCancel} className={btnGhost}>انصراف</button>
        <span className="text-xs text-slate-400">حذف مرحله وجود ندارد؛ برای برداشتن آن از مسیر، وضعیتش را «بایگانی» کنید.</span>
      </div>
      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
    </form>
  )
}

/** One lesson inside a stage: its place, its titles, and the buttons that move it or take it out. */
export function StageLessonRow({ lesson, index, total, busy, onMove, onRemove }) {
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 px-2 py-1.5">
      <span className="text-xs text-slate-400">{faDigits(index + 1)}</span>
      <span className="text-sm text-slate-800" dir="ltr">{lessonName(lesson)}</span>
      {lessonAltName(lesson) && <span className="text-xs text-slate-500">{lessonAltName(lesson)}</span>}
      <StatusBadge status={lesson.status} />
      <span className="text-xs text-slate-400">{faDigits(lesson.sentenceCount)} جمله</span>
      <span className="ms-auto flex items-center gap-1">
        <button type="button" disabled={busy || index === 0} onClick={() => onMove(-1)} className={btnMove} aria-label="بالا">↑</button>
        <button type="button" disabled={busy || index === total - 1} onClick={() => onMove(1)} className={btnMove} aria-label="پایین">↓</button>
        <button type="button" disabled={busy} onClick={onRemove} className={btnGhost}>خارج کردن</button>
      </span>
    </li>
  )
}

/** One stage: its place on the path, its lessons in their order, and the box that adds another lesson to it. */
export function StageCard({ stage, index, total, addable, busy, onMoveStage, onSaveStage, onMoveLesson, onRemoveLesson, onAddLesson }) {
  const [editing, setEditing] = useState(false)
  const [pick, setPick] = useState('')

  return (
    <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-400">مرحلهٔ {faDigits(index + 1)}</span>
        <h3 className="text-sm font-semibold text-slate-800">{stage.titleFa}</h3>
        <span className="text-xs text-slate-500" dir="ltr">{stage.titleEn}</span>
        <StatusBadge status={stage.status} />
        {stage.theme && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500" dir="ltr">{stage.theme}</span>}
        <span className="ms-auto flex items-center gap-1">
          <button type="button" disabled={busy || index === 0} onClick={() => onMoveStage(-1)} className={btnMove} aria-label="بالا">↑</button>
          <button type="button" disabled={busy || index === total - 1} onClick={() => onMoveStage(1)} className={btnMove} aria-label="پایین">↓</button>
          <button type="button" disabled={busy} onClick={() => setEditing(v => !v)} className={btnGhost}>ویرایش</button>
        </span>
      </div>

      {editing && (
        <StageEditForm
          stage={stage}
          busy={busy}
          onCancel={() => setEditing(false)}
          onSave={async patch => { const ok = await onSaveStage(patch); if (ok) setEditing(false) }}
        />
      )}

      {stage.lessons.length === 0
        ? <p className="mt-2 text-xs text-slate-400">این مرحله هنوز درسی ندارد.</p>
        : (
          <ol className="mt-2 space-y-1">
            {stage.lessons.map((lesson, lessonIndex) => (
              <StageLessonRow
                key={lesson.id}
                lesson={lesson}
                index={lessonIndex}
                total={stage.lessons.length}
                busy={busy}
                onMove={delta => onMoveLesson(lessonIndex, delta)}
                onRemove={() => onRemoveLesson(lesson.id)}
              />
            ))}
          </ol>
        )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select value={pick} onChange={e => setPick(e.target.value)} disabled={busy || addable.length === 0} className={inputCls}>
          <option value="">افزودن درس…</option>
          {addable.map(lesson => (
            <option key={lesson.id} value={lesson.id}>{lessonName(lesson)}{lessonAltName(lesson) ? ` — ${lessonAltName(lesson)}` : ''}</option>
          ))}
        </select>
        <button
          type="button"
          disabled={busy || !pick}
          onClick={async () => { const ok = await onAddLesson(pick); if (ok) setPick('') }}
          className={btnGhost}
        >
          افزودن
        </button>
        {addable.length === 0 && <span className="text-xs text-slate-400">همهٔ درس‌های این سطح در مرحله‌ای هستند.</span>}
      </div>
    </section>
  )
}

/**
 * One level's path, given `admin-arcade_listStages`'s answer: the stages in
 * order, each with its lessons, and everything that changes either. Every
 * mutation re-reads the level afterwards — the server owns the order (a
 * reorder renumbers the whole level, an assign only writes one position),
 * so re-reading is the only honest way to show what it now is.
 */
export function StagesBoard({ level, data, settings, onChanged }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const stages = data.items
  const unassigned = data.unassignedLessons.filter(lesson => lesson.level === level)

  /** Runs one mutation, reloads the level, and turns a 422 into Persian. Returns whether it worked. */
  async function run(fn) {
    setBusy(true); setErr('')
    try {
      await fn()
      await onChanged()
      return true
    } catch (ex) {
      setErr(stageRefusal(ex) ?? errorMessage(ex))
      return false
    } finally { setBusy(false) }
  }

  function moveStage(index, delta) {
    return run(() => reorderStages(level, moved(stages, index, delta).map(stage => stage.id)))
  }

  // A lesson's position is not unique on the wire, so the whole stage is
  // renumbered 0..n-1 — only the rows that actually moved are sent.
  function moveLesson(stage, index, delta) {
    const plan = renumberPlan(moved(stage.lessons, index, delta))
    return run(async () => {
      for (const { lessonId, orderInStage } of plan) {
        await assignLesson({ lessonId, stageId: stage.id, orderInStage })
      }
    })
  }

  return (
    <div className="space-y-3">
      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

      <ArcadeRulesCard settings={settings} />
      <UnassignedWarning lessons={unassigned} />
      <StageCreateForm level={level} busy={busy} onCreate={dto => run(() => createStage(dto))} />

      {stages.length === 0
        ? <p className="py-10 text-center text-sm text-slate-400">هنوز مرحله‌ای برای این سطح ساخته نشده است.</p>
        : stages.map((stage, index) => (
          <StageCard
            key={stage.id}
            stage={stage}
            index={index}
            total={stages.length}
            addable={unassigned}
            busy={busy}
            onMoveStage={delta => moveStage(index, delta)}
            onSaveStage={patch => run(() => updateStage(stage.id, patch))}
            onMoveLesson={(lessonIndex, delta) => moveLesson(stage, lessonIndex, delta)}
            onRemoveLesson={lessonId => run(() => assignLesson({ lessonId, stageId: null }))}
            onAddLesson={lessonId => run(() => assignLesson({ lessonId, stageId: stage.id }))}
          />
        ))}
    </div>
  )
}

/**
 * The «مراحل» tab (V7-p): pick a level, then build its Arcade path. The
 * settings are read once for the read-only rules card; a failure to read
 * them leaves the card empty rather than the tab broken, because the path
 * itself is still editable without them.
 */
export function StagesTab() {
  const [level, setLevel] = useState('beginner')
  const [data, setData] = useState(null)
  const [settings, setSettings] = useState(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try { setData(await listStages(level)) }
    catch (ex) { setErr(errorMessage(ex)) }
  }, [level])

  useEffect(() => { setData(null); load() }, [load])
  useEffect(() => { listSettings().then(setSettings).catch(() => setSettings([])) }, [])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-slate-600">سطح</span>
        {LEVELS.map(one => (
          <button
            key={one}
            type="button"
            onClick={() => setLevel(one)}
            className={`rounded-lg px-3 py-1.5 text-sm ${one === level ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-500 hover:bg-slate-100'}`}
          >
            {LEVEL_FA[one]}
          </button>
        ))}
        <button type="button" onClick={load} className={`ms-auto ${btnGhost}`}>تازه‌سازی</button>
      </div>

      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}
      {data === null
        ? <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>
        : <StagesBoard level={level} data={data} settings={settings} onChanged={load} />}
    </div>
  )
}
